import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "typescript",
  name: "TypeScript",
  nameEn: "TypeScript",
  seo: {
    title: "TypeScriptを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "TypeScriptはJavaScriptに型安全性を加えた言語で、大規模開発の標準として企業採用が急増中。ゆめさくエンジニアで実践的に学びましょう。",
    url: "/columns/typescript-programming-school",
  },
  hero: {
    title: "TypeScriptを学べる女性向けプログラミングスクール",
    englishTitle: "Learn TypeScript",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "TypeScript" },
  ],
  overview: {
    heading: "TypeScriptとは？",
    paragraphs: [
      "TypeScriptは、Microsoftが2012年に開発したJavaScriptのスーパーセット（上位互換）言語です。JavaScriptの全機能に加えて「型システム」を導入し、コードの品質と開発効率を大幅に向上させます。",
      "近年、企業のフロントエンド・バックエンド開発でTypeScriptの採用が急速に進んでおり、新規プロジェクトではTypeScriptがデフォルトの選択肢になりつつあります。",
      "型があることでエディタの補完が強力になり、チーム開発でのバグを未然に防げるため、特に中〜大規模なプロジェクトで威力を発揮します。",
    ],
    useCases: [
      {
        label: "大規模Webアプリ開発",
        description:
          "型による安全性で、数十万行規模のコードベースでもバグを最小限に抑えた開発が可能です。",
      },
      {
        label: "チーム開発",
        description:
          "型定義がドキュメントの役割を果たし、他の開発者が書いたコードの理解が容易になります。",
      },
      {
        label: "APIサーバー開発",
        description:
          "NestJSやExpressと組み合わせて、型安全なバックエンドAPIを効率的に構築できます。",
      },
      {
        label: "ライブラリ・SDKの開発",
        description:
          "型定義により、利用者に使いやすいインターフェースを提供できるため、OSSやSDK開発でも広く採用されています。",
      },
    ],
  },
  whyLearn: {
    heading: "TypeScriptを学ぶべき3つの理由",
    items: [
      {
        title: "企業の採用率が急上昇中",
        description:
          "State of JS調査では、TypeScript利用率が年々上昇し、フロントエンド求人の多くがTypeScriptスキルを求めています。JavaScriptだけの求人は減少傾向にあり、TypeScriptの習得は今後のキャリアに必須です。",
      },
      {
        title: "バグを事前に発見でき、品質の高いコードが書ける",
        description:
          "型チェックにより、実行前にエラーを検出できます。「数値を入れるべき場所に文字列を入れてしまった」といったミスをエディタがリアルタイムで指摘してくれるため、デバッグの時間を大幅に削減できます。",
      },
      {
        title: "JavaScriptの知識がそのまま活かせる",
        description:
          "TypeScriptはJavaScriptの完全な上位互換です。JavaScriptで書いたコードはそのままTypeScriptとして動作するため、段階的に型を追加していく学習アプローチが可能です。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでTypeScriptを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではJavaScriptの基礎を固めた後、TypeScriptの型システムを実践的に学びます。単なる文法学習ではなく、Reactと組み合わせた型安全なコンポーネント開発や、APIとの型定義共有など、現場で求められるスキルを習得します。",
      "実務では「既存のJavaScriptプロジェクトをTypeScriptに移行する」場面も多いため、その手法もカリキュラムに含まれています。",
    ],
    topics: [
      "型の基礎（プリミティブ型・オブジェクト型・配列型）",
      "インターフェースと型エイリアス",
      "ジェネリクスの理解と活用",
      "React + TypeScriptによるコンポーネント開発",
      "API通信の型安全な実装",
      "実践プロジェクト：型安全なフルスタックアプリ開発",
    ],
  },
  careerPaths: {
    heading: "TypeScriptを活かせるキャリア",
    items: [
      {
        title: "フロントエンドエンジニア",
        description:
          "React・Next.js・VueなどのモダンフレームワークとTypeScriptを組み合わせて、堅牢なUIを構築します。型の知識がある人材は現場で重宝されます。",
        salary: "500〜800万円",
      },
      {
        title: "フルスタックエンジニア",
        description:
          "TypeScriptの強みは、フロントエンドとバックエンドで同じ言語・同じ型定義を共有できること。Next.jsやNestJSを使った一貫した開発体験で、プロダクト全体を設計・実装できます。",
        salary: "550〜950万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "8〜14ヶ月（JS基礎+TS習得）",
        luagate: "4〜5ヶ月（JSからTSへの段階的移行）",
      },
      {
        label: "挫折率",
        selfStudy: "型エラーの意味がわからず挫折しやすい",
        luagate: "型の考え方を丁寧に指導、段階的に難易度アップ",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "型をつけるメリットを実感しにくい",
        luagate: "チーム開発で型の恩恵を体感できる",
      },
      {
        label: "質問対応",
        selfStudy: "複雑な型エラーの解決に苦労する",
        luagate: "メンターが型設計のベストプラクティスを指導",
      },
      {
        label: "就職サポート",
        selfStudy: "TypeScript経験をアピールする方法がわからない",
        luagate: "TypeScript対応の実務レベルポートフォリオで差別化",
      },
    ],
  },
  faq: [
    {
      question: "TypeScriptはJavaScriptを知らなくても学べますか？",
      answer:
        "TypeScriptはJavaScriptの拡張なので、まずJavaScriptの基礎を理解する必要があります。ゆめさくエンジニアではJavaScriptからTypeScriptへ段階的に進むカリキュラムを提供しているため、スムーズに移行できます。",
    },
    {
      question: "TypeScriptは難しいですか？",
      answer:
        "基本的な型定義は直感的で、JavaScriptの知識があれば数週間で基礎を習得できます。ジェネリクスなどの高度な機能は時間をかけて学べばOKです。ゆめさくエンジニアでは実践を通じて無理なくステップアップできます。",
    },
    {
      question: "TypeScriptは実務で本当に使われていますか？",
      answer:
        "はい、急速に広がっています。Google、Microsoft、Airbnb、Slackなど大手企業をはじめ、国内でもメルカリ、サイバーエージェントなど多くの企業がTypeScriptを採用しています。",
    },
    {
      question: "JavaScriptだけでは就職できませんか？",
      answer:
        "JavaScript単体でも就職は可能ですが、近年の求人ではTypeScriptスキルが求められるケースが増えています。TypeScriptを習得しておくことで、応募できる求人の幅が大きく広がります。",
    },
  ],
  relatedLanguages: [
    { slug: "javascript", name: "JavaScript" },
    { slug: "react", name: "React" },
    { slug: "html-css", name: "HTML/CSS" },
    { slug: "python", name: "Python" },
  ],
};
